import { useMemo, useState } from "react";
import styles from "./BrandingView.module.scss";
import { Pagination, View, ViewHeader } from "@/components";
import type { BrandingScopeFilter, BrandingStatusFilter } from "../../_constants/branding.const";
import {
  DEVICE_BRANDING_PROTOTYPE_SAMPLES,
  createPrototypeBrandingId,
} from "../../_constants/device-branding-prototypes.const";
import type { ApiDeviceBranding } from "../../_types/device-brandings.types";
import { CreateBrandDrawer } from "../create-brand-drawer";
import { BrandDetailDrawer } from "../brand-detail-drawer";
import { BrandingToolbar } from "../branding-toolbar";
import { BrandingsList } from "../brandings-list";
import { DeleteBrandModal } from "../delete-brand-modal";
import { EditBrandDrawer } from "../edit-brand-drawer";

export const BrandingView = () => {
  const [items, setItems] = useState<ApiDeviceBranding[]>(() =>
    DEVICE_BRANDING_PROTOTYPE_SAMPLES.map((b) => ({
      ...b,
      extra: b.extra ? { ...b.extra } : null,
    })),
  );
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [scope, setScope] = useState<BrandingScopeFilter>("all");
  const [status, setStatus] = useState<BrandingStatusFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editBrand, setEditBrand] = useState<ApiDeviceBranding | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ApiDeviceBranding | null>(null);

  const totalPages = Math.max(1, Math.ceil(items.length / limit) || 1);
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * limit;
    return items.slice(start, start + limit);
  }, [items, safePage, limit]);

  const openDetail = (id: string) => {
    setSelectedId(id);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedId(null);
  };

  const openEdit = (b: ApiDeviceBranding) => {
    setEditBrand(b);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditBrand(null);
  };

  const duplicateBranding = (b: ApiDeviceBranding) => {
    const now = Date.now();
    setItems((prev) => [
      {
        ...b,
        id: createPrototypeBrandingId(),
        name: `${b.name} (copy)`,
        extra: b.extra ? { ...b.extra, prototype: true } : { prototype: true },
        configVersion: 1,
        createdAt: now,
        updatedAt: now,
      },
      ...prev,
    ]);
  };

  const confirmDeleteBranding = () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setItems((prev) => prev.filter((b) => b.id !== id));
    setPendingDelete(null);
    if (selectedId === id) closeDetail();
  };

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading="Branding (Prototype / Preview)"
          description="UI-only preview of device visual themes. Sample data lives in component memory and resets on refresh.<br />Create, edit, and details stay local — no API mutations, Assets uploads, or assignment to real devices."
        />

        <BrandingToolbar
          scope={scope}
          status={status}
          onScopeChange={setScope}
          onStatusChange={setStatus}
          onCreateBrandClick={() => setCreateOpen(true)}
        />

        <BrandingsList
          items={pageItems}
          isLoading={false}
          isFetching={false}
          isError={false}
          onRetry={() => {}}
          onRowClick={openDetail}
          onEdit={openEdit}
          onDuplicate={duplicateBranding}
          onRequestDelete={(b) => setPendingDelete(b)}
          actionsDisabled={false}
        />

        {items.length > 0 ? (
          <Pagination
            page={safePage}
            limit={limit}
            total={items.length}
            totalPages={totalPages}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setLimit(next);
              setPage(1);
            }}
          />
        ) : null}

        <CreateBrandDrawer
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreate={(brand) => setItems((prev) => [brand, ...prev])}
        />

        <EditBrandDrawer
          isOpen={editOpen}
          brand={editBrand}
          onClose={closeEdit}
          onSave={(brand) =>
            setItems((prev) => prev.map((b) => (b.id === brand.id ? brand : b)))
          }
        />

        <DeleteBrandModal
          isOpen={pendingDelete !== null}
          brandName={pendingDelete?.name ?? ""}
          onClose={() => setPendingDelete(null)}
          onConfirm={confirmDeleteBranding}
          isDeleting={false}
        />

        <BrandDetailDrawer
          brandId={selectedId}
          brands={items}
          isOpen={detailOpen}
          onClose={closeDetail}
          onRequestDelete={(b) => setPendingDelete(b)}
        />
      </div>
    </View>
  );
};
